<template>
  <div :id="'comment-' + cid" class="comment is-size-7">
    <div class="intro">
      <nav class="level is-mobile">
        <div class="level-left">
          <div class="level-item">
            <span class="initials">{{ initials }}</span
            >{{ userName }}
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">{{ changedFormatted }}</div>
        </div>
      </nav>
    </div>

    <h4 class="title is-5">{{ subject }}</h4>
    <div v-if="body" class="has-text-weight-medium" v-html="body" />
  </div>
</template>

<script setup lang="ts">
import type { CommentCommentFragment } from '#graphql-operations'

const props = defineProps<{
  cid?: number
  changed?: string
  name?: string
  subject?: string
  body?: string
  user?: CommentCommentFragment['user']
}>()

const userName = computed(() => {
  if (props.user?.address?.givenName && props.user?.address?.familyName) {
    return (
      props.user?.address?.givenName + ' ' + props.user?.address?.familyName
    )
  }

  return props.name
})

const initials = computed(() => {
  const names = userName.value?.split(' ') ?? ''
  let initials = ''
  if (names[0]) {
    initials = names[0].substring(0, 1).toUpperCase()
    if (names.length > 1) {
      initials += names[names.length - 1].substring(0, 1).toUpperCase()
    }
  }
  return initials
})

const changedFormatted = computed(() => {
  const changed = props?.changed ?? ''
  const new_Date: Date = new Date(changed)
  return new_Date.toLocaleString('de', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
})
</script>

<style lang="scss">
@import '~/assets/scss/helper/mixins.scss';

.comment {
  .intro {
    font-size: 10px;
    font-weight: bold;
    margin-bottom: rem(20);
  }

  .initials {
    color: $white;
    background-color: $grey-dark;
    height: 32px;
    width: 32px;
    display: inline-block;
    border-radius: 50%;
    margin-right: 1rem;
    line-height: 32px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
  }

  & {
    border-top: 2px solid $grey-lighter;
    padding: 1.5rem 2.5rem 3rem 2.5rem;
    background-color: $grey-lighter;
  }

  &:nth-child(2n) {
    background: transparent;
  }

  &:last-of-type {
    border-bottom: 2px solid $grey-lighter;
  }
}
</style>
